import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getUserInfo, getPermissionsForRole } from "../api";
import { PermissionEnum } from "../../../shared/frontend/enums/PermissionEnum";
import { toPermissionValueSet } from "../../../shared/frontend/utils/permissions";

interface UserContextType {
  role: number | undefined;
  dg: string;
  displayName: string;
  hasPermission: (permission: PermissionEnum) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<number | undefined>(undefined);
  const [dg, setDg] = useState("INTPA");
  const [displayName, setDisplayName] = useState("INTPA");
  const [permissionValues, setPermissionValues] = useState<Set<PermissionEnum>>(new Set());

  useEffect(() => {
    getUserInfo().then((info) => {
      setDg(info.dg);
      setDisplayName(info.displayName ?? info.dg);
      setRole(info.role);
      if (typeof info.role === "number") {
        getPermissionsForRole(info.role).then((names) => {
          const s = toPermissionValueSet(names);
          console.debug("[RBAC] role:", info.role, "permissions:", names, "Set:", s);
          setPermissionValues(s);
        });
      }
    });
  }, []);

  const hasPermission = useCallback(
    (permission: PermissionEnum) => permissionValues.has(permission),
    [permissionValues],
  );

  const value = useMemo(
    () => ({ role, dg, displayName, hasPermission }),
    [role, dg, displayName, hasPermission],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
